import { APP_SETTINGS } from '../config/appSettings'
import { assetPreviewBlob, type NoteAsset } from './assetTypes'

interface RemotePreviewCacheStore {
  cacheRemoteAssetPreview(
    assetId: string,
    storagePath: string,
    blob: Blob,
  ): Promise<NoteAsset | undefined>
}

interface CacheRemoteAssetPreviewsOptions {
  assets: NoteAsset[]
  urls: ReadonlyMap<string, string>
  store: RemotePreviewCacheStore
  fetchPreview?: (url: string) => Promise<Response>
  maxConcurrentDownloads?: number
}

export interface RemotePreviewCacheResult {
  cachedAssets: NoteAsset[]
  failedAssetIds: string[]
}

export function remotePreviewStoragePath(asset: NoteAsset): string | null {
  return asset.thumbnailStoragePath ?? asset.masterStoragePath
}

export function remoteAssetNeedsPreviewCache(asset: NoteAsset): boolean {
  return asset.state === 'synced'
    && !assetPreviewBlob(asset)
    && remotePreviewStoragePath(asset) !== null
}

export function remotePreviewCacheKey(assets: NoteAsset[]): string {
  return assets
    .filter(remoteAssetNeedsPreviewCache)
    .map((asset) => `${asset.id}:${asset.modifiedAt}:${remotePreviewStoragePath(asset)}`)
    .sort()
    .join('|')
}

export function remotePreviewRetryDelay(attempt: number): number {
  const exponent = Math.min(
    Math.max(0, attempt - 1),
    APP_SETTINGS.images.previewCacheRetryExponentCap,
  )
  return Math.min(
    APP_SETTINGS.images.previewCacheRetryMaxMs,
    APP_SETTINGS.images.previewCacheRetryBaseMs * 2 ** exponent,
  )
}

function validImageResponse(response: Response, blob: Blob): boolean {
  const contentType = blob.type || response.headers.get('content-type') || ''
  return response.ok
    && blob.size > 0
    && blob.size <= APP_SETTINGS.images.clientInputSafetyLimitBytes
    && contentType.toLowerCase().startsWith('image/')
}

export async function cacheRemoteAssetPreviews({
  assets,
  urls,
  store,
  fetchPreview = (url) => fetch(url),
  maxConcurrentDownloads = APP_SETTINGS.images.previewCacheMaxConcurrentDownloads,
}: CacheRemoteAssetPreviewsOptions): Promise<RemotePreviewCacheResult> {
  const candidates = assets.filter(remoteAssetNeedsPreviewCache)
  const cachedAssets: NoteAsset[] = []
  const failedAssetIds: string[] = []
  const concurrency = Math.max(1, maxConcurrentDownloads)

  for (let index = 0; index < candidates.length; index += concurrency) {
    const batch = candidates.slice(index, index + concurrency)
    const results = await Promise.all(batch.map(async (asset) => {
      const url = urls.get(asset.id)
      const storagePath = remotePreviewStoragePath(asset)
      if (!url || !storagePath) return null
      try {
        const response = await fetchPreview(url)
        const blob = await response.blob()
        if (!validImageResponse(response, blob)) return null
        const cached = await store.cacheRemoteAssetPreview(asset.id, storagePath, blob)
        return cached && assetPreviewBlob(cached) ? cached : null
      } catch {
        return null
      }
    }))
    results.forEach((cached, batchIndex) => {
      if (cached) cachedAssets.push(cached)
      else failedAssetIds.push(batch[batchIndex]!.id)
    })
  }

  return { cachedAssets, failedAssetIds }
}
